import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { Button } from '../ui/button';
import NotificationBell from '../NotificationBell';
import { MobileBottomNav } from './MobileBottomNav';
import { InstallBanner, OfflineBanner } from './PwaBanners';
import { PushPromptBanner } from './PushPromptBanner';
import { SupportChat } from '../support/SupportChat';
import { flushQueue, readQueue } from '../../lib/offlineQueue';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Building2, 
  HardHat, 
  Menu, 
  X, 
  User, 
  LogOut, 
  LayoutDashboard,
  MessageSquare,
  Settings,
  Shield,
  Globe,
  ChevronDown,
  FolderKanban,
  BarChart3,
  Heart,
  Lightbulb, Newspaper,
  ArrowLeft
} from 'lucide-react';

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇧🇪' },
  { code: 'lu', name: 'Lëtzebuergesch', flag: '🇱🇺' }
];

const MainLayout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const { unreadCount } = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [queuedCount, setQueuedCount] = useState(readQueue().length);

  useEffect(() => {
    const sync = () => setQueuedCount(readQueue().length);
    const onOnline = () => { if (user) flushQueue(); };
    window.addEventListener('bc-offline-queue-change', sync);
    window.addEventListener('online', onOnline);
    if (user && navigator.onLine) flushQueue();
    return () => {
      window.removeEventListener('bc-offline-queue-change', sync);
      window.removeEventListener('online', onOnline);
    };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
  };

  const currentLanguage = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-16 md:pb-0">
      <OfflineBanner />
      {queuedCount > 0 && (
        <div className="bg-[#1E3A8A] text-white text-center text-xs font-medium py-1.5 px-4" data-testid="offline-queue-banner">
          {queuedCount} candidature{queuedCount > 1 ? 's' : ''} en attente d'envoi — {navigator.onLine ? 'envoi en cours…' : 'sera envoyée automatiquement dès le retour du réseau'}
        </div>
      )}
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Back + Logo */}
            <div className="flex items-center">
            {location.pathname !== '/' && (
              <button
                type="button"
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
                className="mr-1 md:mr-3 w-9 h-9 rounded-full flex items-center justify-center text-[#1E3A8A] bg-[#1E3A8A]/5 hover:bg-[#1E3A8A]/10 transition-colors flex-shrink-0"
                aria-label="Retour"
                title="Page précédente"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-[#1E3A8A] rounded-md flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className={`font-heading font-bold text-xl text-[#1E3A8A] ${location.pathname !== '/' ? 'hidden sm:inline' : ''}`}>BuildConnect</span>
            </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link 
                to="/jobs" 
                className={`text-sm font-medium transition-colors ${isActive('/jobs') ? 'text-[#1E3A8A]' : 'text-[#374151] hover:text-[#1E3A8A]'}`}
                data-testid="nav-jobs"
              >
                {t('nav.jobs')}
              </Link>
              <Link 
                to="/contractors" 
                className={`text-sm font-medium transition-colors ${isActive('/contractors') ? 'text-[#1E3A8A]' : 'text-[#374151] hover:text-[#1E3A8A]'}`}
                data-testid="nav-contractors"
              >
                {t('nav.contractors')}
              </Link>
              <Link 
                to="/marches-publics" 
                className={`text-sm font-medium transition-colors ${isActive('/marches-publics') ? 'text-[#1E3A8A]' : 'text-[#374151] hover:text-[#1E3A8A]'}`}
                data-testid="nav-tenders"
              >
                Chantiers publics
              </Link>
              <Link 
                to="/zones-a-construire" 
                className={`text-sm font-medium transition-colors ${isActive('/zones-a-construire') ? 'text-[#1E3A8A]' : 'text-[#374151] hover:text-[#1E3A8A]'}`}
                data-testid="nav-zones"
              >
                Zones à construire
              </Link>
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-1 sm:gap-3">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1 px-2 sm:px-3" data-testid="language-selector">
                    <Globe className="w-4 h-4" />
                    <span className="hidden sm:inline">{currentLanguage.flag}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {LANGUAGES.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code} 
                      onClick={() => changeLanguage(lang.code)}
                      className={i18n.language === lang.code ? 'bg-[#1E3A8A]/10' : ''}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {user ? (
                <>
                  {/* Notifications */}
                  <NotificationBell />

                  {/* Messages shortcut */}
                  <Link to="/dashboard/messages" className="relative hidden md:block">
                    <Button variant="ghost" size="icon" data-testid="nav-messages-btn">
                      <MessageSquare className="w-5 h-5" />
                    </Button>
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] bg-[#F59E0B] text-white rounded-full text-[10px] flex items-center justify-center font-bold leading-none px-1" data-testid="unread-messages-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="gap-2 px-2 sm:px-4" data-testid="user-menu-trigger">
                        <div className="w-8 h-8 bg-[#1E3A8A] rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <span className="hidden sm:inline text-sm font-medium">{user.company_name}</span>
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium">{user.company_name}</p>
                        <p className="text-xs text-[#4B5563]">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      {isAdmin ? (
                        <>
                          <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                            <Shield className="w-4 h-4 mr-2" />
                            {t('nav.admin')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/messages')} data-testid="menu-messages">
                            <MessageSquare className="w-4 h-4 mr-2" />
                            {t('nav.messages')}
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <>
                          <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                            <LayoutDashboard className="w-4 h-4 mr-2" />
                            {t('nav.dashboard')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/profile')} data-testid="menu-profile">
                            <User className="w-4 h-4 mr-2" />
                            {t('nav.profile')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/messages')} data-testid="menu-messages">
                            <MessageSquare className="w-4 h-4 mr-2" />
                            {t('nav.messages')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/projects')} data-testid="menu-projects">
                            <FolderKanban className="w-4 h-4 mr-2" />
                            Projets
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/analytics')} data-testid="menu-analytics">
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Analytics
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/favorites')} data-testid="menu-favorites">
                            <Heart className="w-4 h-4 mr-2" />
                            Favoris
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/insurance')} data-testid="menu-insurance">
                            <Shield className="w-4 h-4 mr-2" />
                            Assurances
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/actualites')} data-testid="menu-news">
                            <Newspaper className="w-4 h-4 mr-2 text-[#1E3A8A]" />
                            Actualités du bâtiment
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dashboard/suggestions')} data-testid="menu-suggestions">
                            <Lightbulb className="w-4 h-4 mr-2 text-[#F59E0B]" />
                            Vos idées pour BuildConnect
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('nav.logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" data-testid="nav-login">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="btn-primary" data-testid="nav-register">{t('nav.register')}</Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#1E3A8A]/10">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/jobs" 
                className="block py-2 text-[#374151] hover:text-[#1E3A8A]"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.jobs')}
              </Link>
              <Link 
                to="/contractors" 
                className="block py-2 text-[#374151] hover:text-[#1E3A8A]"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.contractors')}
              </Link>
              <Link 
                to="/marches-publics" 
                className="block py-2 text-[#374151] hover:text-[#1E3A8A]"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-tenders"
              >
                Chantiers publics
              </Link>
              <Link 
                to="/zones-a-construire" 
                className="block py-2 text-[#374151] hover:text-[#1E3A8A]"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-zones"
              >
                Zones à construire
              </Link>
              {!user && (
                <>
                  <Link 
                    to="/login" 
                    className="block py-2 text-[#374151] hover:text-[#1E3A8A]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.login')}
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="btn-primary w-full">{t('nav.register')}</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#374151] text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-[#1E3A8A] rounded-md flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading font-bold text-xl">BuildConnect</span>
              </div>
              <p className="text-sm text-gray-300">
                La plateforme de référence pour connecter les entreprises du bâtiment en Belgique, Luxembourg et France.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Navigation</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/jobs" className="hover:text-white transition-colors">{t('nav.jobs')}</Link></li>
                <li><Link to="/contractors" className="hover:text-white transition-colors">{t('nav.contractors')}</Link></li>
                <li><Link to="/marches-publics" className="hover:text-white transition-colors">Chantiers publics</Link></li>
                <li><Link to="/zones-a-construire" className="hover:text-white transition-colors">Zones à construire</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors" data-testid="footer-faq">FAQ réglementation BTP</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors" data-testid="footer-blog">Blog</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">{t('nav.register')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Pays</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>🇧🇪 Belgique</li>
                <li>🇱🇺 Luxembourg</li>
                <li>🇫🇷 France</li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>contact@buildconnect.be</li>
                <li>+32 61 46 83 32</li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Légal</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/terms" className="hover:text-white transition-colors">Conditions générales</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Politique de confidentialité</Link></li>
                <li><Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-600 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between text-sm text-gray-400">
            <span>© {new Date().getFullYear()} BuildConnect. Tous droits réservés.</span>
            <Link to="/admin" className="mt-3 sm:mt-0 flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors" data-testid="footer-admin-link">
              <Shield className="w-3.5 h-3.5" />
              Administration
            </Link>
          </div>
        </div>
      </footer>
      <MobileBottomNav />
      <InstallBanner />
      <PushPromptBanner />
      <SupportChat />
    </div>
  );
};

export default MainLayout;
